import { useCallback, useMemo, useState } from 'react';
import { ColumnId, TABLE_COLUMNS, TableColumn } from '../constants';

const STORAGE_KEY = 'journal_visible_columns_v2';

const DEFAULT_VISIBLE: ColumnId[] = TABLE_COLUMNS.filter(c => !c.hidden).map(c => c.id);
const KNOWN = new Set<string>(TABLE_COLUMNS.map(c => c.id));
const REQUIRED: ColumnId[] = TABLE_COLUMNS.filter(c => c.required).map(c => c.id);

function readStored(): ColumnId[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_VISIBLE;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_VISIBLE;
    const ids = parsed.filter((id): id is ColumnId => typeof id === 'string' && KNOWN.has(id));
    return Array.from(new Set<ColumnId>([...REQUIRED, ...ids]));
  } catch {
    return DEFAULT_VISIBLE;
  }
}

function writeStored(ids: ColumnId[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // хранилище недоступно (приватный режим) - набор колонок живёт до перезагрузки
  }
}

/** Видимые колонки таблицы - настройка на браузер пользователя */
export function useColumnVisibility() {
  const [visible, setVisible] = useState<ColumnId[]>(readStored);

  const toggle = useCallback((id: ColumnId) => {
    if (REQUIRED.indexOf(id) !== -1) return;
    setVisible(prev => {
      const next = prev.indexOf(id) !== -1 ? prev.filter(x => x !== id) : [...prev, id];
      writeStored(next);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setVisible(DEFAULT_VISIBLE);
    writeStored(DEFAULT_VISIBLE);
  }, []);

  const columns = useMemo<TableColumn[]>(() => TABLE_COLUMNS.filter(c => visible.indexOf(c.id) !== -1), [visible]);

  return { visible, columns, toggle, reset, isDefault: visible.length === DEFAULT_VISIBLE.length && DEFAULT_VISIBLE.every(id => visible.indexOf(id) !== -1) };
}
